import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  // Tooltip,
  TableRow,
  Skeleton,
  TableCell,
  TableBody,
  TableHead,
  // IconButton,
  Typography,
  TableContainer,
  TablePagination,
  IconButton,
  Collapse,
  Tooltip,
  Stack,
  Chip,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import useUserStore from "store/zustand/user";
import { useTranslation } from "react-i18next";
import useLocalizationStore from "store/zustand/localization";
import { ExpandLess, ExpandMore, Add } from "@mui/icons-material";
import { fetchCustomerCompany } from "store/services/customer.service";
import UserProfile from "components/common/UserProfile";

export const getInitials = (value = "") =>
  value
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const CustomerTable = ({
  onView,
  companyId,
  show,
  refetchCustomers,
  filters,
  onAddAddress,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const { language } = useLocalizationStore();
  const [customers, setCustomers] = useState([]);
  const { user } = useUserStore();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0); // Local page state for pagination
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0); // Local page state for pagination
  const [expandedRows, setExpandedRows] = useState({}); // State for expanded rows
  const formatDateValue = useCallback((value) => {
    if (!value) return "";
    if (typeof value.format === "function") {
      return value.format("YYYY-MM-DD");
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
  }, []);

  const buildDateFilter = useCallback(() => {
    const start = formatDateValue(filters?.startDate);
    const end = formatDateValue(filters?.endDate);
    if (!start || !end) {
      return {};
    }
    return { start_date: start, end_date: end };
  }, [filters?.startDate, filters?.endDate, formatDateValue]);

  useEffect(() => {
    setPage(0);
  }, [filters?.startDate, filters?.endDate]);

  useEffect(() => {
    if (!companyId) return;

    const fetchCustomersData = async () => {
      setLoading(true);
      try {
        const payload = {
          page: page + 1,
          limit: rowsPerPage,
          company_id: user?.company?.id || companyId,
          ...buildDateFilter(),
        };
        const response = await fetchCustomerCompany(payload);
        setCustomers(response?.data?.body?.data || []);
        setTotalCount(response?.data?.body?.total);
      } catch (err) {
        setCustomers([]);
        setTotalCount(0);
        console.error("Error fetching customers", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomersData();
  }, [companyId, page, rowsPerPage, user, refetchCustomers, filters, buildDateFilter]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const rows = customers?.length ? customers : [];

  const handleToggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };
  const direction = language === "ar" ? "row-reverse" : "row";
  return (
    <Box>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        {show && (
          <Box
            px={3}
            py={2}
            borderBottom={1}
            borderColor="divider"
            display="flex"
            justifyContent="space-between"
          >
            <Typography variant="h6" fontWeight={600}>
              {t("customerTable.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("customerTable.showing_customers", { count: rows.length })}
            </Typography>
          </Box>
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("customerTable.no")}</TableCell>
                <TableCell>{t("customerTable.name")}</TableCell>
                <TableCell>{t("customerTable.email")}</TableCell>
                <TableCell>{t("customerTable.phone")}</TableCell>
                <TableCell>{t("customerTable.total_orders")}</TableCell>
                <TableCell>{t("customerTable.address")}</TableCell>
                <TableCell>{t("customerTable.documentAlerts", { defaultValue: "Document Alerts" })}</TableCell>
                <TableCell align="center">{t("companyList.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? // Show skeleton for each row while loading
                  [...new Array(5)].map((_, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Skeleton variant="circular" width={40} height={40} />
                          <Box>
                            <Skeleton width="100px" height={32} />
                            <Skeleton width="60px" height={30} />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Skeleton width="150px" height={30} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="150px" height={30} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="120px" height={30} />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton
                          variant="rectangular"
                          width={24}
                          height={24}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                : rows.map((customer) => (
                    <TableRow key={customer?.id} hover>
                      <TableCell
                        onClick={() => onView(customer)}
                        sx={{
                          cursor: "pointer",
                          ":hover": {
                            color: "blue",
                          },
                        }}
                      >
                        {customer?.customer_code}
                      </TableCell>

                      <TableCell
                        onClick={() => onView(customer)}
                        sx={{
                          cursor: "pointer",
                          ":hover": {
                            color: "blue",
                          },
                        }}
                      >
                        <UserProfile
                          customer={customer}
                          direction={direction === "ar" ? "right" : "left"}
                          secondary={customer?.customer_code}
                        />
                      </TableCell>
                      <TableCell>{customer?.email}</TableCell>
                      <TableCell>{customer?.mobile_number}</TableCell>
                      <TableCell>{customer?.total_orders}</TableCell>
                      <TableCell>
                        {/* Address Management */}
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Box>
                            {customer?.addresses?.length > 0 ? (
                              <>
                                <Box>
                                  {customer?.addresses
                                    ?.slice(0, 1)
                                    .map((address, index) => (
                                      <Typography
                                        key={index}
                                        variant="body2"
                                        noWrap
                                        width={200}
                                      >
                                        <Tooltip
                                          title={`${address?.address} (${address.nationalAddress})`}
                                        >
                                          {address?.address}
                                        </Tooltip>
                                      </Typography>
                                    ))}
                                </Box>
                                {customer?.addresses?.length > 1 && (
                                  <>
                                    <IconButton
                                      onClick={() =>
                                        handleToggleRow(customer.id)
                                      }
                                    >
                                      {expandedRows[customer?.id] ? (
                                        <ExpandLess />
                                      ) : (
                                        <ExpandMore />
                                      )}
                                    </IconButton>
                                    <Collapse in={expandedRows[customer.id]}>
                                      {customer?.addresses
                                        ?.slice(1)
                                        .map((address, index) => (
                                          <Typography
                                            key={index}
                                            variant="body2"
                                            noWrap
                                            width={200}
                                          >
                                            <Tooltip title={address?.address}>
                                              {address?.address}
                                            </Tooltip>
                                          </Typography>
                                        ))}
                                    </Collapse>
                                  </>
                                )}
                              </>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t("customerTable.no_address")}
                              </Typography>
                            )}
                          </Box>
                          {!show && (
                            <Tooltip
                              title={t("customerTable.addAddressTooltip", {
                                defaultValue: "Add an other address",
                              })}
                            >
                              <IconButton
                                size="small"
                                onClick={() => onAddAddress(customer.id)}
                              >
                                <Add />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {customer?.alerts_count > 0 ? (
                          <Chip
                            size="small"
                            label={t("customerTable.due", { count: customer.alerts_count, defaultValue: `${customer.alerts_count} due` })}
                            color={customer?.documents?.some((document) => document.alertStatus === "expired") ? "error" : "warning"}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Tooltip title={t("menu.viewProfile")}>
                            <IconButton
                              size="small"
                              onClick={() => onView(customer)}
                              sx={{ color: "info.main" }}
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {onEdit && (
                            <Tooltip
                              title={t("customerTable.edit", {
                                defaultValue: "Edit",
                              })}
                            >
                              <IconButton
                                size="small"
                                onClick={() => onEdit(customer)}
                                sx={{ color: "warning.main" }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onDelete && (
                            <Tooltip
                              title={t("customerTable.delete", {
                                defaultValue: "Delete",
                              })}
                            >
                              <IconButton
                                size="small"
                                onClick={() => onDelete(customer)}
                                sx={{ color: "error.main" }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {t("customerTable.no_customers_found")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <TablePagination
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </Box>
  );
};

export default CustomerTable;
